import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    CardMedia,
    CircularProgress,
    Alert,
    TextField,
    InputAdornment,
    Stack,
    MenuItem,
    Pagination,
} from '@mui/material';
import { Business, Category } from '@mui/icons-material';
import { apiService, Sponsor } from '../services/api';

const Sponsors: React.FC = () => {
    const { t } = useTranslation('explorar');
    const navigate = useNavigate();
    const [sponsorSearchTerm, setSponsorSearchTerm] = useState('');
    const [debouncedSponsorSearchTerm, setDebouncedSponsorSearchTerm] = useState('');
    const [sponsorCategoryFilter, setSponsorCategoryFilter] = useState<string>('');
    const [page, setPage] = useState(1);
    const [pageSize] = useState(20); // Number of sponsors per page

    // Debounce sponsor search term to avoid excessive filtering
    React.useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSponsorSearchTerm(sponsorSearchTerm);
        }, 300); // 300ms delay

        return () => clearTimeout(timer);
    }, [sponsorSearchTerm]);

    // Sponsors query - using getSponsorsWithQ1Mentions to filter for Q1 verified mentions
    const {
        data: sponsorsList = [],
        isLoading: sponsorsLoading,
        error: sponsorsError,
        isFetching: sponsorsFetching,
    } = useQuery({
        queryKey: ['sponsors-q1-mentions'],
        queryFn: () => apiService.getSponsorsWithQ1Mentions(),
        staleTime: 1000 * 60 * 5, // 5 minutes
        gcTime: 1000 * 60 * 5, // 5 minutes
        retry: 1,
        placeholderData: (previousData) => previousData, // Keep previous data while fetching
    });

    // Extract unique categories from sponsors
    const sponsorCategories = React.useMemo(() => {
        const categories = new Set<string>();
        sponsorsList.forEach((sponsor: Sponsor) => {
            if (sponsor.categories && Array.isArray(sponsor.categories)) {
                sponsor.categories.forEach(category => {
                    if (category && category.trim()) {
                        categories.add(category.trim());
                    }
                });
            }
        });
        return Array.from(categories).sort();
    }, [sponsorsList]);

    // Filter sponsors based on search term and category
    const filteredSponsors = React.useMemo(() => {
        let filtered = sponsorsList;

        // Filter by search term
        if (debouncedSponsorSearchTerm.trim()) {
            const searchLower = debouncedSponsorSearchTerm.toLowerCase();
            filtered = filtered.filter((sponsor: Sponsor) =>
                sponsor.name.toLowerCase().includes(searchLower) ||
                (sponsor.description && sponsor.description.toLowerCase().includes(searchLower)) ||
                (sponsor.categories && sponsor.categories.some(category =>
                    category.toLowerCase().includes(searchLower)
                ))
            );
        }

        // Filter by category
        if (sponsorCategoryFilter) {
            filtered = filtered.filter((sponsor: Sponsor) =>
                sponsor.categories && sponsor.categories.includes(sponsorCategoryFilter)
            );
        }

        return filtered;
    }, [sponsorsList, debouncedSponsorSearchTerm, sponsorCategoryFilter]);

    const totalSponsors = filteredSponsors.length;
    const totalPages = Math.ceil(totalSponsors / pageSize);

    // Get paginated sponsors
    const paginatedSponsors = React.useMemo(() => {
        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        return filteredSponsors.slice(startIndex, endIndex);
    }, [filteredSponsors, page, pageSize]);

    const handleSponsorSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setSponsorSearchTerm(event.target.value);
        setPage(1); // Reset to first page when searching
    }, []);

    const handleSponsorCategoryChange = useCallback((event: React.ChangeEvent<{ value: unknown }>) => {
        setSponsorCategoryFilter(event.target.value as string);
        setPage(1); // Reset to first page when changing category
    }, []);

    const handlePageChange = useCallback((event: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
    }, []);

    const isSponsorsInitialLoading = sponsorsLoading && sponsorsList.length === 0;
    const isSponsorsSearching = sponsorsFetching && sponsorsList.length > 0;

    return (
        <>
            {/* Search Bar and Category Filter */}
            <Box sx={{ mb: 4, display: 'flex', gap: 2, alignItems: 'flex-start', width: '100%' }}>
                <TextField
                    label={t('sponsors.search')}
                    value={sponsorSearchTerm}
                    onChange={handleSponsorSearchChange}
                    InputProps={{
                        endAdornment: isSponsorsSearching ? (
                            <InputAdornment position="end">
                                <CircularProgress size={20} />
                            </InputAdornment>
                        ) : null,
                    }}
                    sx={{ minWidth: 200, flex: 1 }}
                />
                <TextField
                    select
                    label={t('sponsors.category')}
                    value={sponsorCategoryFilter}
                    onChange={handleSponsorCategoryChange}
                    sx={{ minWidth: 150 }}
                >
                    <MenuItem value="">
                        <em>{t('sponsors.allCategories')}</em>
                    </MenuItem>
                    {sponsorCategories.map((category) => (
                        <MenuItem key={category} value={category}>
                            {category}
                        </MenuItem>
                    ))}
                </TextField>
            </Box>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                {t('sponsors.showing', { total: totalSponsors.toLocaleString() })}
            </Typography>

            {sponsorsError && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {t('sponsors.loadError')}
                </Alert>
            )}

            {/* Initial Loading */}
            {isSponsorsInitialLoading && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flex: 1,
                        minHeight: '50vh',
                    }}
                >
                    <CircularProgress />
                </Box>
            )}

            {/* Sponsors Grid */}
            {!isSponsorsInitialLoading && (
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, 1fr)',
                        md: 'repeat(3, 1fr)',
                        lg: 'repeat(4, 1fr)'
                    },
                    gap: 3,
                    mb: 4,
                    position: 'relative',
                }}>
                    {paginatedSponsors.map((sponsor: Sponsor) => (
                        <Box
                            key={sponsor._id}
                            onClick={() => navigate(`/explorar/auspiciadores/${sponsor._id}`)}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden',
                                position: 'relative',
                                transition: 'all 0.2s ease-in-out',
                                cursor: 'pointer',
                                height: '100%',
                                backgroundColor: 'white',
                            }}
                        >
                            {/* Top - Logo/Image */}
                            <Box sx={{ position: 'relative', height: '200px' }}>
                                {sponsor.logo ? (
                                    <CardMedia
                                        component="img"
                                        height="200"
                                        image={sponsor.logo}
                                        alt={sponsor.name}
                                        sx={{
                                            objectFit: 'cover',
                                            width: '100%',
                                            height: '200px',
                                            borderRadius: 2,
                                        }}
                                    />
                                ) : (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            height: '100%',
                                            backgroundColor: 'primary.main',
                                            color: 'white',
                                            borderRadius: 2,
                                        }}
                                    >
                                        <Business sx={{ fontSize: 48 }} />
                                    </Box>
                                )}
                            </Box>

                            {/* Bottom - Content */}
                            <Box
                                sx={{
                                    pt: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    flex: 1,
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Box>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            textAlign: 'left',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            maxWidth: '100%',
                                            fontSize: '1.1rem',
                                            lineHeight: 1.1,
                                            fontWeight: 600,
                                            color: 'primary.main',
                                            mb: 1,
                                        }}
                                    >
                                        {sponsor.name}
                                    </Typography>
                                    {sponsor.description && (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                lineHeight: 1.4,
                                                mb: 2,
                                            }}
                                        >
                                            {sponsor.description}
                                        </Typography>
                                    )}
                                </Box>

                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    spacing={1}
                                    sx={{ width: '100%' }}
                                >
                                    {sponsor.categories && sponsor.categories.length > 0 && (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                            }}
                                        >
                                            <Category sx={{ fontSize: 16, color: 'text.secondary' }} />
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ fontSize: '0.75rem' }}
                                            >
                                                {sponsor.categories[0]}
                                            </Typography>
                                        </Box>
                                    )}
                                </Stack>
                            </Box>
                        </Box>
                    ))}
                </Box>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={handlePageChange}
                        color="primary"
                        size="large"
                    />
                </Box>
            )}
        </>
    );
};

export default Sponsors; 